import React, { useState, useEffect } from 'react';
import { adminApi } from '../utils/api';

const RED = '#C5161D';
const card = { background: '#fff', borderRadius: 12, border: '1px solid #E2E8F0', padding: '20px 24px' };

export const ReferralsPage = () => {
  const [stats, setStats]         = useState({});
  const [referrals, setReferrals] = useState([]);
  const [total, setTotal]         = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  // Per-user detail modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [userInfo, setUserInfo]         = useState(null);
  const [adjustPoints, setAdjustPoints] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjusting, setAdjusting]       = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [s, r] = await Promise.all([
        adminApi.getReferralStats(),
        adminApi.getReferrals(statusFilter ? `?status=${statusFilter}` : ''),
      ]);
      setStats(s);
      setReferrals(r.referrals || []);
      setTotal(r.total || 0);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line

  const openUser = async (userId) => {
    setSelectedUser(userId);
    setUserInfo(null);
    try {
      const info = await adminApi.getUserReferralInfo(userId);
      setUserInfo(info);
    } catch (e) {
      setUserInfo({ error: e.message });
    }
  };

  const handleAdjust = async () => {
    const pts = parseInt(adjustPoints, 10);
    if (!pts || !adjustReason.trim()) return;
    setAdjusting(true);
    try {
      await adminApi.adjustUserPoints(selectedUser, pts, adjustReason.trim());
      const info = await adminApi.getUserReferralInfo(selectedUser);
      setUserInfo(info);
      setAdjustPoints('');
      setAdjustReason('');
      load();
    } catch (e) {
      alert(e.message);
    } finally {
      setAdjusting(false);
    }
  };

  const handleFlag = async (id, current) => {
    try { await adminApi.flagReferral(id, !current); load(); } catch (e) { console.error(e); }
  };

  const STAT_CARDS = [
    { label: 'Total Referrals',    value: stats.totalReferrals,    color: '#7C3AED' },
    { label: 'Signed Up',          value: stats.signedUp,          color: '#2563EB' },
    { label: 'Converted (Pro)',     value: stats.converted,         color: '#15803D' },
    { label: 'Points Awarded',     value: stats.totalPointsAwarded, color: '#D97706' },
    { label: 'Points Redeemed',    value: stats.totalPointsRedeemed, color: RED },
  ];

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 800, color: '#0F172A' }}>Referral Management</h2>
        <p style={{ margin: 0, color: '#64748B', fontSize: 13 }}>Track referrals, points, conversions and flag abuse.</p>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
        {STAT_CARDS.map(sc => (
          <div key={sc.label} style={{ ...card, flex: '1 1 160px', minWidth: 140 }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: sc.color }}>{sc.value ?? '—'}</div>
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>{sc.label}</div>
          </div>
        ))}
      </div>

      {/* Top referrers */}
      {stats.topReferrers?.length > 0 && (
        <div style={{ ...card, marginBottom: 24 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Top Referrers</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {stats.topReferrers.map((r, i) => (
              <div key={r._id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F8FAFC', borderRadius: 8, padding: '8px 12px', border: '1px solid #E2E8F0' }}>
                <span style={{ fontWeight: 800, color: '#94A3B8', fontSize: 12 }}>#{i + 1}</span>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>{r.name || 'Unknown'}</div>
                  <div style={{ fontSize: 11, color: '#64748B' }}>{r.count} referrals</div>
                </div>
                <button onClick={() => openUser(r._id)} style={{ marginLeft: 8, fontSize: 11, background: '#EFF6FF', color: '#2563EB', border: 'none', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontWeight: 600 }}>
                  Details
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Referrals table */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>All Referrals ({total})</div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ fontSize: 12, border: '1px solid #E2E8F0', borderRadius: 7, padding: '5px 10px', background: '#fff', cursor: 'pointer' }}>
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="signed_up">Signed Up</option>
            <option value="converted">Converted</option>
          </select>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: 20, color: RED, fontSize: 13 }}>{error}</div>
        ) : referrals.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>No referrals found</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #F1F5F9' }}>
                  {['Referrer','Referred User','Status','Signup Pts','Purchase Pts','Fraud','Actions'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: '#64748B', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {referrals.map((r, i) => (
                  <tr key={r._id} style={{ borderBottom: '1px solid #F8FAFC', background: r.fraudFlag ? '#FFF7F7' : i % 2 === 0 ? '#FAFAFA' : '#fff' }}>
                    <td style={{ padding: '10px 10px' }}>
                      <div style={{ fontWeight: 600, color: '#0F172A' }}>{r.referrerId?.name || '—'}</div>
                      <div style={{ fontSize: 11, color: '#94A3B8' }}>{r.referrerId?.email}</div>
                    </td>
                    <td style={{ padding: '10px 10px' }}>
                      <div style={{ fontWeight: 500, color: '#334155' }}>{r.referredUserName || r.referredUserEmail || '—'}</div>
                      <div style={{ fontSize: 11, color: '#94A3B8' }}>{r.referredUserEmail}</div>
                    </td>
                    <td style={{ padding: '10px 10px' }}>
                      <span style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 700,
                        background: r.status === 'converted' ? '#DCFCE7' : r.status === 'signed_up' ? '#EFF6FF' : '#F1F5F9',
                        color: r.status === 'converted' ? '#15803D' : r.status === 'signed_up' ? '#2563EB' : '#64748B',
                      }}>
                        {r.status}
                      </span>
                    </td>
                    <td style={{ padding: '10px 10px', textAlign: 'center', color: r.signupPointsAwarded ? '#15803D' : '#CBD5E1', fontWeight: 700 }}>
                      {r.signupPointsAwarded ? '✓ 5' : '—'}
                    </td>
                    <td style={{ padding: '10px 10px', textAlign: 'center', color: r.purchasePointsAwarded ? '#15803D' : '#CBD5E1', fontWeight: 700 }}>
                      {r.purchasePointsAwarded ? '✓ 15' : '—'}
                    </td>
                    <td style={{ padding: '10px 10px', textAlign: 'center' }}>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight: 700, background: r.fraudFlag ? '#FEE2E2' : '#F0FDF4', color: r.fraudFlag ? RED : '#15803D' }}>
                        {r.fraudFlag ? 'Flagged' : 'Clean'}
                      </span>
                    </td>
                    <td style={{ padding: '10px 10px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {r.referrerId?._id && (
                          <button onClick={() => openUser(r.referrerId._id)} style={{ fontSize: 11, background: '#EFF6FF', color: '#2563EB', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontWeight: 600 }}>
                            Points
                          </button>
                        )}
                        <button onClick={() => handleFlag(r._id, r.fraudFlag)} style={{ fontSize: 11, background: r.fraudFlag ? '#FEF9C3' : '#FEE2E2', color: r.fraudFlag ? '#92400E' : RED, border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontWeight: 600 }}>
                          {r.fraudFlag ? 'Unflag' : 'Flag'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Per-user detail modal */}
      {selectedUser && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 520, width: '100%', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0F172A' }}>User Referral Detail</h3>
              <button onClick={() => { setSelectedUser(null); setUserInfo(null); }} style={{ background: '#F1F5F9', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Close</button>
            </div>

            {!userInfo ? (
              <div style={{ textAlign: 'center', padding: 32, color: '#94A3B8' }}>Loading…</div>
            ) : userInfo.error ? (
              <div style={{ color: RED, fontSize: 13 }}>{userInfo.error}</div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                  {[
                    { label: 'Active Points', value: userInfo.activePoints ?? 0, color: '#7C3AED' },
                    { label: 'Total Earned', value: userInfo.totalEarned ?? 0, color: '#15803D' },
                    { label: 'Total Redeemed', value: userInfo.totalRedeemed ?? 0, color: RED },
                  ].map(s => (
                    <div key={s.label} style={{ flex: 1, background: '#F8FAFC', borderRadius: 10, padding: '12px 14px', border: '1px solid #E2E8F0', textAlign: 'center' }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Recent transactions */}
                {userInfo.recentTransactions?.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8, color: '#0F172A' }}>Recent Transactions</div>
                    {userInfo.recentTransactions.map(tx => (
                      <div key={tx._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px', borderRadius: 8, marginBottom: 4, background: '#F8FAFC', border: '1px solid #F1F5F9' }}>
                        <div>
                          <span style={{ fontSize: 12, color: '#334155' }}>{tx.description}</span>
                          <div style={{ fontSize: 10, color: '#94A3B8' }}>{new Date(tx.createdAt).toLocaleDateString()}</div>
                        </div>
                        <span style={{ fontWeight: 700, fontSize: 14, color: tx.points > 0 ? '#15803D' : RED }}>
                          {tx.points > 0 ? '+' : ''}{tx.points}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Manual adjustment */}
                <div style={{ background: '#FFFBEB', borderRadius: 10, padding: '14px 16px', border: '1px solid #FDE68A' }}>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: '#92400E' }}>Manual Point Adjustment</div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                    <input type="number" placeholder="Points (e.g. 10 or -5)" value={adjustPoints} onChange={e => setAdjustPoints(e.target.value)}
                      style={{ flex: 1, border: '1px solid #E2E8F0', borderRadius: 7, padding: '7px 10px', fontSize: 13 }} />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input type="text" placeholder="Reason (required)" value={adjustReason} onChange={e => setAdjustReason(e.target.value)}
                      style={{ flex: 1, border: '1px solid #E2E8F0', borderRadius: 7, padding: '7px 10px', fontSize: 13 }} />
                    <button onClick={handleAdjust} disabled={adjusting || !adjustPoints || !adjustReason.trim()}
                      style={{ background: RED, color: 'white', border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: adjusting ? 0.6 : 1 }}>
                      {adjusting ? '…' : 'Apply'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
